import React, { useState, useEffect } from 'react'
import {
  Input,
  Modal,
  Button,
  Radio,
  Divider,
  Card,
  Typography,
  notification,
} from 'antd'
import styled from 'styled-components/macro'

const { Title, Text } = Typography

const ALLERGEN_OPTIONS = [
  'Celery',
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Lupin',
  'Milk',
  'Molluscs',
  'Mustard',
  'Nuts',
  'Peanuts',
  'Sesame seeds',
  'Soya',
  'Sulphites',
]

const CustomProduct = ({ isModalVisible, setIsModalVisible, handleChange }) => {
  const [addingProduct, setAddingProduct] = useState({
    title: '',
    size: '',
    price: '',
    sku: '',
    UoM: '',
    totalPrice: '',
    allergens: [],
    allergenTraces: [],
    allergensEditedByUser: false,
  })

  const handleAllergenChange = (allergen, value) => {
    if (value === 'yes') {
      setAddingProduct(prev => ({
        ...prev,
        allergens: [...prev.allergens.filter(a => a !== allergen), allergen],
        allergenTraces: prev.allergenTraces.filter(a => a !== allergen),
      }))
    } else if (value === 'traces') {
      setAddingProduct(prev => ({
        ...prev,
        allergens: prev.allergens.filter(a => a !== allergen),
        allergenTraces: [
          ...prev.allergenTraces.filter(a => a !== allergen),
          allergen,
        ],
      }))
    } else {
      setAddingProduct(prev => ({
        ...prev,
        allergens: prev.allergens.filter(a => a !== allergen),
        allergenTraces: prev.allergenTraces.filter(a => a !== allergen),
      }))
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleSave = () => {
    if (!addingProduct.title?.trim()) {
      notification.error({
        message: 'Title required',
        description: 'Please enter a title for the custom ingredient.',
        duration: 5,
      })
      return
    }

    // Set allergensEditedByUser to true if any allergens are selected
    const hasAllergens =
      addingProduct.allergens.length > 0 ||
      addingProduct.allergenTraces.length > 0
    const parsedPrice = Number(addingProduct.price)
    const pricePounds = Number.isFinite(parsedPrice) ? parsedPrice : undefined
    const productToSave = {
      ...addingProduct,
      title: addingProduct.title.trim(),
      allergensEditedByUser: hasAllergens,
      ...(pricePounds !== undefined ? { pricePounds } : {}),
    }

    handleChange('customProducts', prev => [...(prev || []), productToSave])

    setAddingProduct({
      title: '',
      size: '',
      price: '',
      sku: '',
      UoM: '',
      totalPrice: '',
      allergens: [],
      allergenTraces: [],
      allergensEditedByUser: false,
    })
    setIsModalVisible(false)
  }

  // Update totalPrice whenever size or price changes
  useEffect(() => {
    const size = parseFloat(addingProduct.size)
    const price = parseFloat(addingProduct.price)
    const isValid = !isNaN(size) && !isNaN(price)
    const total = isValid ? (size * price).toFixed(2) : ''
    setAddingProduct(prev => ({
      ...prev,
      totalPrice: total,
    }))
  }, [addingProduct.size, addingProduct.price])

  return (
    <Modal
      title='Provide Product Information'
      visible={isModalVisible}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key='submit' type='primary' onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <CardComp>
        <h3>Provide information about the custom product:</h3>

        <InputComp
          value={addingProduct.title}
          addonBefore={'Title'}
          onChange={e => {
            setAddingProduct({ ...addingProduct, title: e.target.value })
          }}
          placeholder='Title'
        />

        <InputComp
          type='number'
          value={addingProduct.size}
          addonBefore={'Size'}
          onChange={e => {
            setAddingProduct({ ...addingProduct, size: e.target.value })
          }}
          placeholder='Size'
        />

        <InputComp
          type='number'
          value={addingProduct.price}
          addonBefore={'£'}
          onChange={e => {
            setAddingProduct({ ...addingProduct, price: e.target.value })
          }}
          placeholder='Customer Price'
        />

        <InputComp
          value={addingProduct.UoM}
          addonBefore={'UoM'}
          onChange={e => {
            setAddingProduct({ ...addingProduct, UoM: e.target.value })
          }}
          placeholder='Unit of Measure'
        />

        <InputComp
          value={addingProduct.sku}
          addonBefore={'SKU'}
          onChange={e => {
            setAddingProduct({ ...addingProduct, sku: e.target.value })
          }}
          placeholder='SKU'
        />

        <Divider>Allergens</Divider>

        <div style={{ marginBottom: '16px' }}>
          <Title level={4}>Allergen Information</Title>
          <Text
            type='secondary'
            style={{ marginBottom: '16px', display: 'block' }}
          >
            Select allergens present in this custom product. Choose
            &quot;Yes&quot; for definite presence, &quot;Traces&quot; for
            possible traces, or &quot;No&quot; for no presence.
          </Text>

          <div style={{ display: 'flex', gap: '20px' }}>
            {/* Left side - Allergen selection */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px',
                }}
              >
                {ALLERGEN_OPTIONS.map(allergen => {
                  const hasAllergen = addingProduct.allergens.includes(allergen)
                  const hasTraces =
                    addingProduct.allergenTraces.includes(allergen)
                  const currentValue = hasAllergen
                    ? 'yes'
                    : hasTraces
                    ? 'traces'
                    : 'no'

                  return (
                    <Card
                      key={allergen}
                      size='small'
                      style={{ padding: '8px' }}
                    >
                      <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                        {allergen}
                      </div>
                      <Radio.Group
                        value={currentValue}
                        onChange={e =>
                          handleAllergenChange(allergen, e.target.value)
                        }
                        size='small'
                      >
                        <Radio value='no'>No</Radio>
                        <Radio value='traces'>May Contain</Radio>
                        <Radio value='yes'>Yes</Radio>
                      </Radio.Group>
                    </Card>
                  )
                })}
              </div>
            </div>

            {/* Right side - Information box */}
            <div style={{ flex: 0.4 }}>
              <InfoCard>
                <Title
                  level={5}
                  style={{
                    color: '#b4d820',
                    marginBottom: '12px',
                    fontSize: '14px',
                  }}
                >
                  Allergen Information
                </Title>
                <Text style={{ fontSize: '12px', lineHeight: '1.4' }}>
                  <strong>Yes:</strong> The allergen is definitely present in
                  this product.
                  <br />
                  <br />
                  <strong>May Contain:</strong> The allergen may contain traces
                  due to cross-contamination or shared equipment.
                  <br />
                  <br />
                  <strong>No:</strong> The allergen is not present in this
                  product.
                </Text>
              </InfoCard>
            </div>
          </div>
        </div>

        {/* Show Total Price */}
        {addingProduct.totalPrice && (
          <TotalPriceText>
            Total Price: £{addingProduct.totalPrice}
          </TotalPriceText>
        )}
      </CardComp>
    </Modal>
  )
}

const CardComp = styled.div``

const InputComp = styled(Input)`
  margin-bottom: 12px;
`

const TotalPriceText = styled.div`
  font-weight: bold;
  margin-top: 10px;
`

const InfoCard = styled(Card)`
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;

  .ant-card-body {
    padding: 16px;
  }
`

export default CustomProduct
